import { useMemo, useState } from 'react'

import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { IconButton } from '@mui/material'
import { IconSparkles } from '@tabler/icons-react'
import type { DayProperty, ObjectPropertyDefinition } from 'types/graphql'
import { isUUID } from 'validator'

import PropertyDisplay from 'src/components/Properties/PropertyDisplay'
import PropertyEdit from 'src/components/Properties/PropertyEdit'
import Row from 'src/components/Row/Row'
import { logger } from 'src/lib/logger'
import type { NativeObjectType } from 'src/lib/objects'
import {
  StandardProperties,
  StandardPropertyEditPolicy,
  StandardPropertyTablePolicy,
} from 'src/lib/Properties/properties'
import { hasPropertyValue } from 'src/lib/utils'

import useObject from '../useObject'
import useObjects from '../useObjects'

import SidebarHeader from './SidebarHeader'
import SidebarTabs from './SidebarTabs'
import useSidebar from './useSidebar'

const StandardPropertyRenderer = ({
  propertyDefinitionId,
  value,
}: {
  propertyDefinitionId: string
  value: DayProperty
}) => {
  const { propertyDefinitions } = useObjects()
  const { object, workspaceId } = useObject()
  const { examineProperty, updateProperty } = useSidebar()

  const standardPropDef =
    StandardProperties?.[object?.objectType]?.[propertyDefinitionId]

  const propertyDefinition = propertyDefinitions?.[object?.objectType]?.[
    propertyDefinitionId
  ] as ObjectPropertyDefinition

  if (!propertyDefinition) {
    logger.dev('Property definition missing', {
      objectType: object?.objectType,
      propertyDefinitionId,
    })
    return null
  }

  return (
    object &&
    propertyDefinition && (
      <Row className="property-row">
        <Box className="property-edit-container">
          <Box className="property-edit-inner">
            {standardPropDef.editPolicy ===
              StandardPropertyEditPolicy.ALLOW && (
              <PropertyEdit
                propertyDefinition={propertyDefinition}
                objectId={object.objectId}
                objectType={object.objectType}
                workspaceId={workspaceId}
                value={value}
                justify="start"
                onUpdate={(updatedProperty) => {
                  updateProperty(updatedProperty)
                }}
                onClose={() => {}}
                showEditButton={true}
              />
            )}

            {standardPropDef.editPolicy ===
              StandardPropertyEditPolicy.CONTEXT_ONLY && (
              <IconButton
                onClick={() => {
                  examineProperty(propertyDefinition.id)
                }}
              >
                <IconSparkles />
              </IconButton>
            )}
          </Box>
        </Box>
        <Row className="property-row-content">
          <Typography className="property-name">
            {propertyDefinition.name}
          </Typography>
          <Box className="property-value">
            <PropertyDisplay
              propertyDefinition={propertyDefinition}
              objectId={object.objectId}
              objectType={object.objectType as NativeObjectType}
              workspaceId={object.workspaceId}
              value={value}
              justify="flex-start"
            />
          </Box>
        </Row>
      </Row>
    )
  )
}

const SidebarProperties = () => {
  const { object } = useObject()
  const { propertyDefinitions } = useObjects()
  const [filter, setFilter] = useState<
    'all' | 'populated' | 'custom' | 'standard'
  >('all')

  const populatedProperties = useMemo(
    () => [
      ...Object.keys(object.properties.standard).filter((property) =>
        hasPropertyValue(object.properties.standard[property])
      ),
      ...Object.keys(object.properties.custom).filter((property) =>
        hasPropertyValue(object.properties.custom[property])
      ),
    ],
    [object.properties.standard, object.properties.custom]
  )

  const allProperties = useMemo(() => {
    const hiddenStandardProperties = Object.keys(
      StandardProperties?.[object.objectType] || {}
    ).filter(
      (property) =>
        StandardProperties?.[object.objectType]?.[property]?.tablePolicy ===
        StandardPropertyTablePolicy.HIDE
    )
    const standardPropertyIds = Object.keys(
      propertyDefinitions?.[object.objectType] || {}
    ).filter(
      (property) =>
        !hiddenStandardProperties.includes(property) && !isUUID(property)
    )
    const customPropertyIds = Object.keys(
      propertyDefinitions?.[object.objectType] || {}
    ).filter((property) => isUUID(property))
    return [...standardPropertyIds, ...customPropertyIds]
  }, [propertyDefinitions, object.objectType])

  const customProperties = useMemo(() => {
    return Object.keys(propertyDefinitions?.[object.objectType] || {}).filter(
      (property) => isUUID(property)
    )
  }, [propertyDefinitions, object.objectType])

  const standardProperties = useMemo(() => {
    return Object.keys(propertyDefinitions?.[object.objectType] || {}).filter(
      (property) => !isUUID(property)
    )
  }, [propertyDefinitions, object.objectType])

  const propertiesToShow = useMemo(() => {
    let candidatePropertyIds = allProperties
    if (filter === 'all') {
      candidatePropertyIds = allProperties
    } else if (filter === 'populated') {
      candidatePropertyIds = populatedProperties
    } else if (filter === 'custom') {
      candidatePropertyIds = customProperties
    } else if (filter === 'standard') {
      logger.dev('standardProperties', { standardProperties })
      candidatePropertyIds = standardProperties
    }

    return candidatePropertyIds.filter((propertyId) =>
      allProperties.includes(propertyId)
    )
  }, [
    filter,
    allProperties,
    populatedProperties,
    customProperties,
    standardProperties,
  ])

  const populatedPropertiesToShow = useMemo(() => {
    return Object.keys(object.properties.custom)
      .filter((property) => {
        return hasPropertyValue(object.properties.custom[property])
      })
      .map((property) => {
        return object.properties.custom[property]?.value
      })
  }, [object.properties.custom])

  logger.dev('propertiesToShow', {
    propertiesToShow,
    allProperties,
    populatedProperties,
    customProperties,
    standardProperties,
    populatedPropertiesToShow,
  })

  return (
    object && (
      <Box className="section">
        <Row className="section-header-row">
          <ToggleButtonGroup
            value={filter}
            onChange={(_, value) => setFilter(value)}
            exclusive
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="populated">Populated</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
            <ToggleButton value="standard">Standard</ToggleButton>
          </ToggleButtonGroup>
        </Row>
        <Box
          className="properties"
          key={`properties-${filter}`}
        >
          {propertiesToShow.map((propertyDefinitionId) => (
            <SidebarPropertyRenderer
              key={`${filter}-${propertyDefinitionId}`}
              propertyDefinitionId={propertyDefinitionId}
              value={
                object.properties.standard[propertyDefinitionId] ||
                object.properties.custom[propertyDefinitionId] ||
                null
              }
            />
          ))}
        </Box>
      </Box>
    )
  )
}

const SidebarCustomPropertyRenderer = ({
  propertyDefinitionId,
  value,
}: {
  propertyDefinitionId: string
  value: DayProperty
}) => {
  const { propertyDefinitions } = useObjects()
  const { object, workspaceId } = useObject()
  const { updateProperty } = useSidebar()

  const propertyDefinition = useMemo(
    () =>
      propertyDefinitions?.[object.objectType]?.[
        propertyDefinitionId
      ] as ObjectPropertyDefinition,
    [propertyDefinitions, object.objectType, propertyDefinitionId]
  )

  return propertyDefinition ? (
    <Row className="property-row">
      <Box className="property-edit-container">
        <Box className="property-edit-inner">
          <PropertyEdit
            propertyDefinition={propertyDefinition}
            objectId={object.objectId}
            objectType={object.objectType}
            workspaceId={workspaceId}
            value={value}
            justify="start"
            onUpdate={(updatedProperty) => {
              updateProperty(updatedProperty)
            }}
            onClose={() => {}}
            showEditButton={true}
          />
        </Box>
      </Box>
      <Row className="property-row-content">
        <Typography className="property-name">
          {propertyDefinition.name}
        </Typography>
        <Box className="property-value">
          <PropertyDisplay
            propertyDefinition={propertyDefinition}
            objectId={object.objectId}
            objectType={object.objectType as NativeObjectType}
            workspaceId={object.workspaceId}
            value={value}
            justify="flex-start"
          />
        </Box>
      </Row>
    </Row>
  ) : null
}

const SidebarPropertyRenderer = ({
  propertyDefinitionId,
  value,
}: {
  propertyDefinitionId: string
  value: DayProperty
}) => {
  const isCustomProperty = isUUID(propertyDefinitionId)

  return isCustomProperty ? (
    <SidebarCustomPropertyRenderer
      propertyDefinitionId={propertyDefinitionId}
      value={value}
    />
  ) : (
    <StandardPropertyRenderer
      propertyDefinitionId={propertyDefinitionId}
      value={value}
    />
  )
}

const SidebarModeView = () => {
  const { object } = useObject()

  return (
    object && (
      <Box className="sidebar-content-mode-view">
        <SidebarHeader />
        <SidebarTabs />
        <Box className="sidebar-content-mode-view-inner">
          <SidebarProperties />
        </Box>
      </Box>
    )
  )
}

export default SidebarModeView
